import type { BaseResponse, EmptyResponse, HttpClient, Result } from '@/lib/http/HttpClient';
import type {
  CodeVerificationRequest as VerifyCodeRequest,
  EmailVerificationRequest as VerifyEmailRequest,
  LoginRequest,
  ChangePasswordRequest,
  RegisterRequest,
} from './AuthRequest';
import type {
  IsAdminResponse,
  LoginResponse,
  ProfileResponse,
  RefreshResponse,
  RegisterResponse,
} from './AuthResponse';

export class AuthApiClient {
  private readonly http: HttpClient;

  constructor(http: HttpClient) {
    this.http = http;
  }

  private async postWithCreds<T extends BaseResponse>(
    url: string,
    data: unknown = {}
  ): Promise<Result<T>> {
    return this.http.post<T>(url, data, { credentials: 'include' });
  }

  async isAdmin(): Promise<Result<IsAdminResponse>> {
    return this.http.post<IsAdminResponse>('/api/auth/isAdmin');
  }

  async login(data: LoginRequest): Promise<Result<LoginResponse>> {
    return this.postWithCreds<LoginResponse>('/api/auth/login', data);
  }

  async refresh(): Promise<Result<RefreshResponse>> {
    return this.postWithCreds<RefreshResponse>('/api/auth/refresh');
  }

  async logout(): Promise<void> {
    await this.postWithCreds<BaseResponse>('/api/auth/logout');
  }

  async verifyToken(): Promise<Result<EmptyResponse>> {
    return this.http.post<EmptyResponse>('/api/auth/verifyToken');
  }

  async verifyEmail(data: VerifyEmailRequest): Promise<Result<EmptyResponse>> {
    return this.http.post<EmptyResponse>('/api/auth/email-verification', data);
  }

  async verifyCode(data: VerifyCodeRequest): Promise<Result<EmptyResponse>> {
    return this.http.post<EmptyResponse>('/api/auth/verify-code', data);
  }

  async changePassword(data: ChangePasswordRequest): Promise<Result<EmptyResponse>> {
    return this.http.post<EmptyResponse>('/api/auth/changePassword', data);
  }

  async register(data: RegisterRequest): Promise<Result<RegisterResponse>> {
    return this.http.post<RegisterResponse>('/api/auth/register', data);
  }

  async getProfile(options?: RequestInit): Promise<Result<ProfileResponse>> {
    return this.http.post<ProfileResponse>('/api/auth/profile', {}, options);
  }
}
